import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { DpColumnId, DpColumnProfile } from '../vacationDpTable.definitions';
import {
    mergeStoredPreferences,
    moveColumnInOrder,
    readFromLocalStorage,
    writeToLocalStorage,
    isCustomized,
    applyPreferenceUpdate,
    type StoredColumnPreferences,
} from '../vacationDpColumnPreferences.utils';

export interface UseDpColumnPreferencesResult {
    columnOrder: DpColumnId[];
    columnVisibility: Record<DpColumnId, boolean>;
    toggleColumn: (id: DpColumnId) => void;
    moveColumn: (activeId: DpColumnId, overId: DpColumnId) => void;
    resetToDefault: () => void;
    isCustomized: boolean;
}

export function useDpColumnPreferences(profile: DpColumnProfile): UseDpColumnPreferencesResult {
    const { user } = useAuth();
    const email = user?.email ?? '';

    const [stored, setStored] = useState<StoredColumnPreferences | null>(() => {
        if (!email) return null;
        return readFromLocalStorage(email);
    });

    useEffect(() => {
        if (!email) {
            setStored(null);
            return;
        }
        const loaded = readFromLocalStorage(email);
        setStored(loaded);
    }, [email]);

    useEffect(() => {
        if (email && stored) {
            writeToLocalStorage(email, stored);
        }
    }, [email, stored]);

    const preferences = useMemo(
        () => mergeStoredPreferences(stored, profile),
        [stored, profile]
    );

    const customized = useMemo(
        () => isCustomized(preferences.order, preferences.visibility, profile),
        [preferences, profile]
    );

    const toggleColumn = useCallback(
        (id: DpColumnId) => {
            setStored((prev) =>
                applyPreferenceUpdate(prev, profile, (prefs) => ({
                    ...prefs,
                    visibility: { ...prefs.visibility, [id]: !prefs.visibility[id] },
                }))
            );
        },
        [profile]
    );

    const moveColumn = useCallback(
        (activeId: DpColumnId, overId: DpColumnId) => {
            setStored((prev) =>
                applyPreferenceUpdate(prev, profile, (prefs) => ({
                    ...prefs,
                    order: moveColumnInOrder(prefs.order, activeId, overId),
                }))
            );
        },
        [profile]
    );

    const resetToDefault = useCallback(() => {
        setStored((prev) => {
            const updated: StoredColumnPreferences = { ...prev };
            delete updated[profile];
            return updated;
        });
    }, [profile]);

    return {
        columnOrder: preferences.order,
        columnVisibility: preferences.visibility,
        toggleColumn,
        moveColumn,
        resetToDefault,
        isCustomized: customized,
    };
}
